import React, {
  useEffect,
  useState,
  Fragment,
  useRef,
  SyntheticEvent,
} from 'react';
import classnames from 'classnames';
import { Button } from 'components';
import { createUseStyles, useTheme } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { Logger } from '../lib/logger';
import { MenuWindow, MenuItem } from '@app/core';

const useStyles = createUseStyles((theme: Theme) => ({
  select: {
    position: 'relative',
  },
  button: {
    paddingRight: theme.marginBase * 2,
    paddingLeft: theme.marginBase * 2,
  },
  caret: {
    fontSize: theme.iconSize.regular,
    marginLeft: theme.marginBase / 2,
  },
}));

interface Option {
  value: string;
  label?: string;
  icon?: string;
  className?: string;
}

interface Props {
  onChange: (value: string, option: Option) => any;
  value: string;
  options: Option[];
  className?: string;
  children?: React.ReactNode | React.ReactNodeArray;
  color?: Colors;
}

export const Dropdown = ({
  value,
  options,
  onChange,
  className,
  children,
  color,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const [option, setOption] = useState<Option | null>(null);
  const [anchorElmnt, setAnchorElmnt] = useState<null | Element>(null);
  const idRef = useRef(`dots_menu_${Math.random()}`);

  const onClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    setAnchorElmnt(event.currentTarget);
  };
  const onClose = (event: SyntheticEvent) => {
    event.stopPropagation();
    setAnchorElmnt(null);
  };

  useEffect(() => {
    const foundOption = options.find((o) => o.value === value);
    if (!foundOption || foundOption === option) {
      return;
    }
    setOption(foundOption);
  }, [options, value, option]);

  const onSelect = (val: string) => {
    const newOption = options.find((o) => o.value === val);
    if (!newOption) {
      Logger.assertionFailed('Option not found', { newOption, val });
      return;
    }
    return onChange(val, newOption);
  };

  return (
    <Fragment>
      <Button
        variant="muted"
        color={color || 'blue'}
        className={classnames(classes.button, className)}
        onClick={onClick}
        aria-controls={idRef.current}
        aria-haspopup="true"
        xsmall
      >
        {option && option.label}
        <i className={classnames('ri-arrow-down-s-line', classes.caret)} />
      </Button>
      <MenuWindow
        open={!!anchorElmnt}
        onClose={onClose}
        onClick={onClose}
        anchorEl={anchorElmnt}
        id={idRef.current}
      >
        {options.map((sOption) => (
          <MenuItem
            value={sOption.value}
            key={sOption.value}
            label={sOption.label}
            selected={sOption.value === value}
            onSelect={onSelect}
            className={sOption.className}
          />
        ))}
        {children}
      </MenuWindow>
    </Fragment>
  );
};
